import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { casesAPI, adminAPI, referralAPI, signaturesAPI, userManagementAPI } from '../utils/api';
import { Link } from 'react-router-dom';
import { DollarSign, Users, CheckCircle, Clock, Plus, LogOut, Search, UserPlus, Gift, FileText, Edit2, Trash2, X, Check, XCircle, Copy, FileCheck, PenTool, AlertCircle, Bell, UserCog, ListTodo } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import TemplateLibrary from './TemplateLibrary';
import TemplateManager from './TemplateManager';
import MetricCard from '../components/MetricCard';
import CasesModal from '../components/CasesModal';
import ReferralsModal from '../components/ReferralsModal';
import SignedDocuments from '../components/SignedDocuments';
import SignerManagement from '../components/SignerManagement';
import ESignatureModal from '../components/ESignatureModal';
import AdminPOAReview from '../components/AdminPOAReview';
import SignatureWorkflow from '../components/SignatureWorkflow';
import { poaAPI, messagesAPI } from '../utils/api';
import { BlinkingDot } from '../components/UnreadBadge';
import AdminTaskBoard from '../components/AdminTaskBoard';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [activeTab, setActiveTab] = useState('cases');
  const [showCasesModal, setShowCasesModal] = useState(false);
  const [modalCases, setModalCases] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [financialSummary, setFinancialSummary] = useState(null);
  const [showReferralsModal, setShowReferralsModal] = useState(false);
  const [modalReferrals, setModalReferrals] = useState([]);
  const [referralModalTitle, setReferralModalTitle] = useState('');
  const [pendingSignatures, setPendingSignatures] = useState(0);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedDocForSign, setSelectedDocForSign] = useState(null);
  const [loginNotifications, setLoginNotifications] = useState([]);
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);
  const [pendingPOAs, setPendingPOAs] = useState(0);
  const [unreadByCase, setUnreadByCase] = useState({});
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [caseToReassign, setCaseToReassign] = useState(null);
  const [adminList, setAdminList] = useState([]);
  const [selectedNewAdmin, setSelectedNewAdmin] = useState('');

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const promises = [
        casesAPI.getCases(),
        adminAPI.getStats(),
        referralAPI.getDashboardStats(),
        referralAPI.getMerchants(),
        signaturesAPI.getPendingReview().catch(() => ({ data: [] })),
        poaAPI.getPendingReviews().catch(() => ({ data: [] })),
        messagesAPI.getUnreadByCase().catch(() => ({ data: {} }))
      ];
      
      // Load login notifications for super admin
      if (isSuperAdmin) {
        promises.push(userManagementAPI.getLoginNotifications().catch(() => ({ data: [] })));
      }
      
      const results = await Promise.all(promises);
      const [casesRes, statsRes, referralStatsRes, referralsRes, pendingRes, pendingPOARes, unreadRes] = results;
      
      setCases(casesRes.data);
      setStats(statsRes.data);
      setReferralStats(referralStatsRes.data);
      setReferrals(referralsRes.data);
      setPendingSignatures(pendingRes.data?.length || 0);
      setPendingPOAs(pendingPOARes.data?.length || 0);
      setUnreadByCase(unreadRes.data || {});
      
      if (isSuperAdmin && results[7]) {
        setLoginNotifications(results[7].data || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleMetricClick = async (filterType, title) => {
    try {
      const response = await adminAPI.getCasesByFilter(filterType);
      setModalCases(response.data.cases);
      setModalTitle(title);
      setFinancialSummary(response.data.financial_summary || null);
      setShowCasesModal(true);
    } catch (error) {
      toast.error('Failed to load cases');
    }
  };

  const handleReferralMetricClick = async (filterType, title) => {
    try {
      const response = await referralAPI.getReferralsByFilter(filterType);
      setModalReferrals(response.data.referrals);
      setReferralModalTitle(title);
      setShowReferralsModal(true);
    } catch (error) {
      toast.error('Failed to load referrals');
    }
  };

  const handleAcceptReferral = async (merchantId) => {
    try {
      const response = await referralAPI.acceptMerchant(merchantId);
      toast.success(
        <div>
          <p className="font-semibold">Referral accepted successfully!</p>
          {response.data.case_id && (
            <p className="text-sm mt-1">Case created and added to active cases</p>
          )}
        </div>,
        { duration: 4000 }
      );
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept referral');
    }
  };

  const handleRejectReferral = async (merchantId) => {
    try {
      await referralAPI.rejectMerchant(merchantId);
      toast.success('Referral rejected');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject referral');
    }
  };

  const handleCloneCase = async (caseId) => {
    try {
      const response = await casesAPI.cloneCase(caseId);
      toast.success(
        <div>
          <p className="font-semibold">Case cloned for attorney review!</p>
          <p className="text-sm mt-1">Review copy created with pending status</p>
        </div>,
        { duration: 4000 }
      );
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone case');
    }
  };

  const handleAttorneyApprove = async (caseId) => {
    try {
      await casesAPI.attorneyApprove(caseId);
      toast.success('Case approved by attorney!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve case');
    }
  };

  const handleOpenReassignModal = async (caseItem) => {
    setCaseToReassign(caseItem);
    try {
      const response = await adminAPI.getAdminList();
      setAdminList(response.data.filter(admin => admin.id !== caseItem.assigned_admin_id));
      setShowReassignModal(true);
    } catch (error) {
      toast.error('Failed to load admin list');
    }
  };

  const handleReassignCase = async () => {
    if (!selectedNewAdmin || !caseToReassign) {
      toast.error('Please select an admin to reassign to');
      return;
    }
    try {
      await casesAPI.reassignCase(caseToReassign.id, selectedNewAdmin);
      toast.success('Case reassigned successfully! Commission rights transferred.');
      setShowReassignModal(false);
      setCaseToReassign(null);
      setSelectedNewAdmin('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign case');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-badge status-pending',
      in_progress: 'status-badge status-in-progress',
      under_review: 'status-badge bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-semibold',
      resolved: 'status-badge status-resolved',
      closed: 'status-badge status-closed',
      accepted: 'status-badge bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold',
      rejected: 'status-badge bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-semibold'
    };
    return statusClasses[status] || statusClasses.pending;
  };

  const filteredCases = cases.filter(c => 
    c.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.client_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="app-layout min-h-screen">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="reserverelease.AI" className="h-12 sm:h-14 object-contain" />
            <div className="flex flex-col">
              <span className="text-lg sm:text-xl md:text-2xl font-bold">
                <span className="bg-gradient-to-r from-purple-600 via-cyan-600 to-purple-600 bg-clip-text text-transparent">
                  ReserveRelease
                </span>
                <span className="text-slate-900">.AI</span>
              </span>
              <span className="text-xs text-slate-500 hidden sm:block">Admin Panel</span>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            {/* Login Notifications Bell (Super Admin Only) */}
            {isSuperAdmin && (
              <div className="relative">
                <button
                  onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
                  className={`relative p-2 rounded-lg transition-colors ${
                    loginNotifications.length > 0 
                      ? 'text-amber-600 hover:bg-amber-50' 
                      : 'text-slate-400 hover:bg-slate-100'
                  }`}
                  title="Admin Login Notifications"
                >
                  <Bell size={22} />
                  {loginNotifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                      {loginNotifications.length}
                    </span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {showNotificationsDropdown && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
                    <div className="p-3 border-b border-slate-200 flex justify-between items-center">
                      <h4 className="font-semibold text-slate-900">Admin Logins</h4>
                      {loginNotifications.length > 0 && (
                        <button
                          onClick={async () => {
                            await userManagementAPI.clearNotifications();
                            setLoginNotifications([]);
                            toast.success('Notifications cleared');
                          }}
                          className="text-xs text-slate-500 hover:text-red-600"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {loginNotifications.length === 0 ? (
                        <div className="p-4 text-center text-slate-500 text-sm">
                          No recent admin logins
                        </div>
                      ) : (
                        loginNotifications.map((notif) => (
                          <div 
                            key={notif.id} 
                            className="p-3 border-b border-slate-100 hover:bg-slate-50 flex items-start gap-3"
                          >
                            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 animate-pulse">
                              <UserPlus size={16} className="text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900">{notif.user_name}</p>
                              <p className="text-xs text-slate-500 truncate">{notif.user_email}</p>
                              <p className="text-xs text-slate-400">
                                {format(new Date(notif.login_time), 'MMM d, h:mm a')}
                              </p>
                            </div>
                            <button
                              onClick={async () => {
                                await userManagementAPI.markNotificationRead(notif.id);
                                setLoginNotifications(prev => prev.filter(n => n.id !== notif.id));
                              }}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <Link 
              to="/settings" 
              className="text-slate-600 hover:text-purple-600 transition-colors font-medium"
              data-testid="settings-link"
            >
              {user?.full_name}
              {isSuperAdmin && (
                <span className="ml-1 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">Super</span>
              )}
            </Link>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="admin-dashboard-title">Admin Dashboard</h1>
            <p className="text-slate-600">Manage all client cases and recovery progress</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="primary-button flex items-center gap-2"
            data-testid="create-case-btn"
          >
            <Plus size={20} />
            Create New Case
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-slate-200">
          <button
            onClick={() => setActiveTab('cases')}
            className={`pb-3 px-4 font-semibold transition-colors ${
              activeTab === 'cases'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="admin-cases-tab"
          >
            Cases & Statistics
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`pb-3 px-4 font-semibold transition-colors flex items-center gap-2 ${
              activeTab === 'templates'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="admin-templates-tab"
          >
            <FileText size={18} />
            Templates & Documents
          </button>
          <button
            onClick={() => setActiveTab('template-manager')}
            className={`pb-3 px-4 font-semibold transition-colors flex items-center gap-2 ${
              activeTab === 'template-manager'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="admin-template-manager-tab"
          >
            <FileText size={18} />
            Manage Templates
          </button>
          <button
            onClick={() => setActiveTab('signatures')}
            className={`pb-3 px-4 font-semibold transition-colors flex items-center gap-2 ${
              activeTab === 'signatures'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="admin-signatures-tab"
          >
            <PenTool size={18} />
            E-Signatures
            {pendingSignatures > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <AlertCircle size={12} />
                {pendingSignatures}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('poa')}
            className={`pb-3 px-4 font-semibold transition-colors flex items-center gap-2 ${
              activeTab === 'poa'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="admin-poa-tab"
          >
            <FileCheck size={18} />
            Power of Attorney
            {pendingPOAs > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse">
                <AlertCircle size={12} />
                {pendingPOAs}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`pb-3 px-4 font-semibold transition-colors flex items-center gap-2 ${
              activeTab === 'tasks'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="admin-tasks-tab"
          >
            <ListTodo size={18} />
            All Tasks
          </button>
        </div>

        {/* Cases Tab Content */}
        {activeTab === 'cases' && (
          <>
            {/* Stats Grid with Clickable Metrics */}
            {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div
              onClick={() => handleMetricClick('all', 'All Clients')}
              className="dashboard-card cursor-pointer hover:border-purple-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <Users className="text-purple-600" size={24} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stats.total_clients}</div>
              <div className="text-sm text-slate-600 mt-1">Total Clients</div>
              <div className="text-xs text-purple-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view all →</div>
            </div>

            <div
              onClick={() => handleMetricClick('all', 'All Cases')}
              className="dashboard-card cursor-pointer hover:border-blue-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <DollarSign className="text-blue-600" size={24} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stats.total_cases}</div>
              <div className="text-sm text-slate-600 mt-1">Total Cases</div>
              <div className="text-xs text-blue-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view all →</div>
            </div>

            <div
              onClick={() => handleMetricClick('active', 'Active Cases')}
              className="dashboard-card cursor-pointer hover:border-amber-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                  <Clock className="text-amber-600" size={24} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stats.active_cases}</div>
              <div className="text-sm text-slate-600 mt-1">Active Cases</div>
              <div className="text-xs text-amber-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view all →</div>
            </div>

            <div
              onClick={() => handleMetricClick('resolved', 'Resolved Cases')}
              className="dashboard-card cursor-pointer hover:border-green-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stats.resolved_cases}</div>
              <div className="text-sm text-slate-600 mt-1">Resolved Cases</div>
              <div className="text-xs text-green-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view all →</div>
            </div>
          </div>
        )}

        {/* Attorney Review Metrics */}
        {stats && (stats.attorney_review_cases > 0 || stats.attorney_approved_cases > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div
              onClick={() => handleMetricClick('attorney_review', 'Pending Attorney Review')}
              className="dashboard-card cursor-pointer hover:border-orange-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                  <FileCheck className="text-orange-600" size={24} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stats.attorney_review_cases}</div>
              <div className="text-sm text-slate-600 mt-1">Pending Attorney Review</div>
              <div className="text-xs text-orange-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view all →</div>
            </div>

            <div
              onClick={() => handleMetricClick('attorney_approved', 'Attorney Approved')}
              className="dashboard-card cursor-pointer hover:border-teal-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                  <CheckCircle className="text-teal-600" size={24} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stats.attorney_approved_cases}</div>
              <div className="text-sm text-slate-600 mt-1">Attorney Approved</div>
              <div className="text-xs text-teal-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view all →</div>
            </div>
          </div>
        )}

        {/* Referral Metrics */}
        {stats && (stats.referral_cases > 0 || stats.accepted_referrals > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div
              onClick={() => handleMetricClick('referral', 'Referral Cases')}
              className="dashboard-card cursor-pointer hover:border-indigo-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                  <Gift className="text-indigo-600" size={24} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stats.referral_cases}</div>
              <div className="text-sm text-slate-600 mt-1">Referral Cases</div>
              <div className="text-xs text-indigo-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view all →</div>
            </div>

            <div
              onClick={() => handleMetricClick('accepted_referral', 'Accepted Referrals')}
              className="dashboard-card cursor-pointer hover:border-emerald-300 hover:shadow-lg transition-all group"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                  <CheckCircle className="text-emerald-600" size={24} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{stats.accepted_referrals || 0}</div>
              <div className="text-sm text-slate-600 mt-1">Accepted Referrals</div>
              <div className="text-xs text-emerald-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view all →</div>
            </div>

            <div className="dashboard-card bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-purple-200 flex items-center justify-center">
                  <UserPlus className="text-purple-600" size={24} />
                </div>
              </div>
              <div className="text-2xl font-bold text-purple-900">{referralStats?.total_referrals || 0}</div>
              <div className="text-sm text-purple-700 mt-1">Total Referral Merchants</div>
            </div>
          </div>
        )}

        {/* Pending Referrals for Review */}
        {referrals && referrals.filter(r => r.status === 'pending').length > 0 && (
          <div className="dashboard-card mb-8 border-amber-200 bg-amber-50">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <UserPlus className="text-amber-600" size={20} />
              Pending Referrals - Awaiting Review
            </h3>
            <div className="space-y-3">
              {referrals.filter(r => r.status === 'pending').map((referral) => (
                <div key={referral.id} className="bg-white rounded-lg p-4 border border-amber-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{referral.merchant_company_name}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 text-sm">
                        <div>
                          <span className="text-slate-500">Contact:</span>
                          <p className="font-medium">{referral.merchant_contact_name}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Reserves:</span>
                          <p className="font-medium">${referral.held_reserves_amount?.toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Processor:</span>
                          <p className="font-medium">{referral.processor_name}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Referred By:</span>
                          <p className="font-medium">{referral.referrer_name}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleAcceptReferral(referral.id)}
                        className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        <Check size={16} />
                        Accept
                      </button>
                      <button
                        onClick={() => handleRejectReferral(referral.id)}
                        className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                      >
                        <XCircle size={16} />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referral Analytics */}
        {referralStats && referralStats.total_referrals > 0 && (
          <div className="dashboard-card mb-8 bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center">
                <Gift className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Referral Program Analytics</h2>
                <p className="text-sm text-slate-600">Track all referrals and commission payouts</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6">
              <div
                onClick={() => handleReferralMetricClick('all', 'All Referrals')}
                className="bg-white rounded-lg p-4 border border-slate-200 cursor-pointer hover:border-purple-300 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <UserPlus className="text-purple-600 group-hover:scale-110 transition-transform" size={18} />
                  <div className="text-xs text-slate-600">Total Referrals</div>
                </div>
                <div className="text-2xl font-bold text-slate-900">{referralStats.total_referrals}</div>
                <div className="text-xs text-purple-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</div>
              </div>
              
              <div
                onClick={() => handleReferralMetricClick('pending', 'Pending Referrals')}
                className="bg-white rounded-lg p-4 border border-slate-200 cursor-pointer hover:border-amber-300 hover:shadow-lg transition-all group"
              >
                <div className="text-xs text-slate-600 mb-2">Pending Review</div>
                <div className="text-2xl font-bold text-amber-600 group-hover:scale-110 transition-transform">{referralStats.pending_referrals}</div>
                <div className="text-xs text-amber-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</div>
              </div>
              
              <div
                onClick={() => handleReferralMetricClick('accepted', 'Accepted Referrals')}
                className="bg-white rounded-lg p-4 border border-slate-200 cursor-pointer hover:border-green-300 hover:shadow-lg transition-all group"
              >
                <div className="text-xs text-slate-600 mb-2">Accepted</div>
                <div className="text-2xl font-bold text-green-600 group-hover:scale-110 transition-transform">{referralStats.accepted_referrals}</div>
                <div className="text-xs text-green-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</div>
              </div>
              
              <div
                onClick={() => handleReferralMetricClick('commission_pending', 'Commissions Pending')}
                className="bg-white rounded-lg p-4 border border-slate-200 cursor-pointer hover:border-cyan-300 hover:shadow-lg transition-all group"
              >
                <div className="text-xs text-slate-600 mb-2">Total Commissions</div>
                <div className="text-2xl font-bold text-cyan-600 group-hover:scale-110 transition-transform">
                  ${referralStats.total_commission_earned.toLocaleString()}
                </div>
                <div className="text-xs text-cyan-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</div>
              </div>
              
              <div
                onClick={() => handleReferralMetricClick('commission_paid', 'Commissions Paid')}
                className="bg-white rounded-lg p-4 border border-slate-200 cursor-pointer hover:border-purple-300 hover:shadow-lg transition-all group"
              >
                <div className="text-xs text-slate-600 mb-2">Commissions Paid</div>
                <div className="text-2xl font-bold text-purple-600 group-hover:scale-110 transition-transform">
                  ${referralStats.total_commission_paid.toLocaleString()}
                </div>
                <div className="text-xs text-purple-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</div>
              </div>
            </div>

            {referralStats.commission_pending > 0 && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-slate-900">Pending Commission Payments</span>
                  <span className="text-xl font-bold text-amber-600">
                    ${referralStats.commission_pending.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cases List */}
        <div className="dashboard-card">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-slate-900">All Cases</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search cases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                data-testid="search-input"
              />
            </div>
          </div>
          
          {loading ? (
            <div className="text-center py-12 text-slate-600">Loading cases...</div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 mb-4">No cases found</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="primary-button"
              >
                Create First Case
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Company</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Created By</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Assigned To</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Frozen Amount</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCases.map((caseItem) => (
                    <tr key={caseItem.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`case-row-${caseItem.id}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-slate-900">{caseItem.company_name}</div>
                          {unreadByCase[caseItem.id] > 0 && (
                            <BlinkingDot active={true} size="sm" />
                          )}
                          {caseItem.is_referral && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                              REFERRAL
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{caseItem.processor_name}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm text-slate-600">{caseItem.client_name}</div>
                        <div className="text-xs text-slate-500">{caseItem.client_email}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm text-slate-600">{caseItem.created_by_name || 'System'}</div>
                        <div className="text-xs text-slate-400">
                          {caseItem.created_at ? format(new Date(caseItem.created_at), 'MMM dd, yyyy') : ''}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm text-slate-600">{caseItem.assigned_admin_name || 'Unassigned'}</div>
                        {caseItem.reassignment_history?.length > 0 && (
                          <div className="text-xs text-amber-600">Reassigned</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-semibold text-slate-900">${caseItem.frozen_amount.toLocaleString()}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={getStatusBadge(caseItem.status)}>
                          {caseItem.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/case/${caseItem.id}`}
                            className="text-purple-600 hover:text-purple-800 font-semibold text-sm"
                          >
                            View
                          </Link>
                          <button
                            onClick={() => handleCloneCase(caseItem.id)}
                            className="text-cyan-600 hover:text-cyan-800 p-1"
                            title="Clone for attorney review"
                          >
                            <Copy size={16} />
                          </button>
                          {caseItem.status === 'pending_attorney_review' && (
                            <button
                              onClick={() => handleAttorneyApprove(caseItem.id)}
                              className="text-teal-600 hover:text-teal-800 p-1"
                              title="Attorney approve"
                            >
                              <FileCheck size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedCase(caseItem);
                              setShowEditModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Edit case"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleOpenReassignModal(caseItem)}
                            className="text-amber-600 hover:text-amber-800 p-1"
                            title="Reassign case to another admin"
                          >
                            <UserCog size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCase(caseItem);
                              setShowDeleteModal(true);
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete case"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}
        </>
      )}

      {/* Templates Tab Content */}
      {activeTab === 'templates' && (
        <TemplateLibrary cases={cases} referrals={referrals} />
      )}

      {/* Template Manager Tab Content */}
      {activeTab === 'template-manager' && (
        <TemplateManager />
      )}

      {/* E-Signatures Tab Content */}
      {activeTab === 'signatures' && (
        <div className="space-y-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                <PenTool className="text-white" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 mb-2">E-Signature Management</h3>
                <p className="text-sm text-slate-700">
                  Drag and drop authorized signers to sign pending documents. Includes POAs and referral agreements.
                  Completed documents are automatically backed up to Dropbox.
                </p>
              </div>
            </div>
          </div>

          {/* Signature Workflow with Drag & Drop */}
          <div className="dashboard-card">
            <SignatureWorkflow 
              onStatsUpdate={(newStats) => {
                setPendingSignatures(newStats.pendingReview);
              }}
            />
          </div>

          {/* Authorized Signers Management Section */}
          <div className="dashboard-card">
            <SignerManagement />
          </div>

          {/* All Signed Documents History */}
          <div className="dashboard-card">
            <SignedDocuments isAdmin={true} />
          </div>
        </div>
      )}

      {/* POA Tab Content */}
      {activeTab === 'poa' && (
        <div className="space-y-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                <FileCheck className="text-white" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 mb-2">Limited Power of Attorney Management</h3>
                <p className="text-sm text-slate-700">
                  Review and counter-sign POA documents from clients. Once both signatures are complete, 
                  the document is automatically backed up to Dropbox and the client is notified.
                </p>
              </div>
            </div>
          </div>

          {/* Pending POA Alert */}
          {pendingPOAs > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-4">
              <AlertCircle className="text-amber-600" size={24} />
              <div className="flex-1">
                <p className="font-semibold text-amber-800">
                  {pendingPOAs} POA document{pendingPOAs !== 1 ? 's' : ''} awaiting your signature
                </p>
                <p className="text-sm text-amber-700">Review and counter-sign to proceed with reserve recovery</p>
              </div>
            </div>
          )}

          {/* POA Documents List */}
          <div className="dashboard-card">
            <AdminPOAReview isAdmin={true} />
          </div>
        </div>
      )}

      {/* Tasks Tab Content */}
      {activeTab === 'tasks' && (
        <div className="dashboard-card">
          <AdminTaskBoard cases={cases} />
        </div>
      )}
      </div>

      {/* Create Case Modal */}
      {showCreateModal && (
        <CreateCaseModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadData();
          }}
        />
      )}

      {/* Edit Case Modal */}
      {showEditModal && selectedCase && (
        <EditCaseModal
          caseData={selectedCase}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCase(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedCase(null);
            loadData();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCase && (
        <DeleteCaseModal
          caseData={selectedCase}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedCase(null);
          }}
          onSuccess={() => {
            setShowDeleteModal(false);
            setSelectedCase(null);
            loadData();
          }}
        />
      )}

      {/* Cases Modal for Metric Drill-Down */}
      {showCasesModal && (
        <CasesModal
          cases={modalCases}
          title={modalTitle}
          onClose={() => setShowCasesModal(false)}
          isAdmin={true}
          financialSummary={financialSummary}
        />
      )}

      {/* Referrals Modal for Referral Analytics Drill-Down */}
      {showReferralsModal && (
        <ReferralsModal
          referrals={modalReferrals}
          title={referralModalTitle}
          onClose={() => setShowReferralsModal(false)}
          isAdmin={true}
        />
      )}

      {/* Reassign Case Modal */}
      {showReassignModal && caseToReassign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-slate-900">Reassign Case</h2>
                <button onClick={() => setShowReassignModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-1">Case</p>
                <p className="font-semibold text-slate-900">{caseToReassign.company_name}</p>
                <p className="text-sm text-slate-500">Client: {caseToReassign.client_name}</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Reassigning this case will transfer commission rights to the new case manager.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select New Case Manager *
                </label>
                <select
                  value={selectedNewAdmin}
                  onChange={(e) => setSelectedNewAdmin(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                >
                  <option value="">Select an admin...</option>
                  {adminList.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.full_name} ({admin.email}) - {admin.role}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowReassignModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReassignCase}
                  disabled={!selectedNewAdmin}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reassign Case
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CreateCaseModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    client_email: '',
    company_name: '',
    frozen_amount: '',
    freeze_date: format(new Date(), 'yyyy-MM-dd'),
    processor_name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Generate a client_id if not provided
      const caseData = {
        ...formData,
        client_id: formData.client_id || `client_${Date.now()}`,
        frozen_amount: parseFloat(formData.frozen_amount),
        freeze_date: new Date(formData.freeze_date).toISOString()
      };

      await casesAPI.createCase(caseData);
      toast.success('Case created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create case');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">Create New Case</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Client Name *</label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Client Email *</label>
              <input
                type="email"
                name="client_email"
                value={formData.client_email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Company Name *</label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Frozen Amount ($) *</label>
              <input
                type="number"
                name="frozen_amount"
                value={formData.frozen_amount}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Freeze Date *</label>
              <input
                type="date"
                name="freeze_date"
                value={formData.freeze_date}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Processor Name *</label>
            <input
              type="text"
              name="processor_name"
              value={formData.processor_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="e.g., Stripe, Square, PayPal"
              required
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 primary-button py-3 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditCaseModal = ({ caseData, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    client_id: caseData.client_id || '',
    client_name: caseData.client_name || '',
    client_email: caseData.client_email || '',
    company_name: caseData.company_name || '',
    frozen_amount: caseData.frozen_amount || '',
    freeze_date: caseData.freeze_date ? format(new Date(caseData.freeze_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    processor_name: caseData.processor_name || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        ...formData,
        client_id: formData.client_id || caseData.client_id,
        frozen_amount: parseFloat(formData.frozen_amount),
        freeze_date: new Date(formData.freeze_date).toISOString()
      };

      await casesAPI.updateCase(caseData.id, updateData);
      toast.success('Case updated successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update case');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-slate-900">Edit Case</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Client Name *</label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Client Email *</label>
              <input
                type="email"
                name="client_email"
                value={formData.client_email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Company Name *</label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Frozen Amount ($) *</label>
              <input
                type="number"
                name="frozen_amount"
                value={formData.frozen_amount}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Freeze Date *</label>
              <input
                type="date"
                name="freeze_date"
                value={formData.freeze_date}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Processor Name *</label>
            <input
              type="text"
              name="processor_name"
              value={formData.processor_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              required
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 primary-button py-3 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteCaseModal = ({ caseData, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setLoading(true);
    try {
      await casesAPI.deleteCase(caseData.id);
      toast.success('Case deleted successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete case');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-red-600">Delete Case</h2>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800 font-semibold mb-2">⚠️ Warning: This action cannot be undone!</p>
            <p className="text-sm text-red-700">
              Deleting this case will permanently remove:
            </p>
            <ul className="list-disc list-inside text-sm text-red-700 mt-2 space-y-1">
              <li>All case documents</li>
              <li>Timeline events</li>
              <li>Tasks and notes</li>
              <li>Messages and reports</li>
            </ul>
          </div>

          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2">Case Details:</p>
            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              <p className="text-sm"><strong>Company:</strong> {caseData.company_name}</p>
              <p className="text-sm"><strong>Client:</strong> {caseData.client_name}</p>
              <p className="text-sm"><strong>Amount:</strong> ${caseData.frozen_amount.toLocaleString()}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Type <strong className="text-red-600">DELETE</strong> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
              placeholder="DELETE"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              disabled={loading || confirmText !== 'DELETE'}
            >
              {loading ? 'Deleting...' : 'Delete Case'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
